import { FC, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Minus } from 'lucide-react';
import { Product } from '../../types/pos.types';
import { posService } from '@/services/pos.service';

interface PresentationOption {
  id: string;
  name: string;
  price: number;
  description?: string;
}

interface PresentationSelectorProps {
  isOpen: boolean;
  product: Product | null;
  deliveryOnly?: boolean;
  onClose: () => void;
  onSelectPresentation: (product: Product, presentationData: PresentationOption, quantity: number) => void;
}

export const PresentationSelector: FC<PresentationSelectorProps> = ({
  isOpen,
  product,
  deliveryOnly = false,
  onClose,
  onSelectPresentation
}) => {
  const [presentations, setPresentations] = useState<PresentationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  // Cargar presentaciones del producto cuando se abre el modal
  useEffect(() => {
    if (isOpen && product) {
      loadPresentations();
      // Reinicializar cantidad cuando se abre el modal
      setQuantity(1);
    } else if (!isOpen) {
      // Limpiar estado cuando se cierra el modal
      setQuantity(1);
      setPresentations([]);
      setError(null);
    }
  }, [isOpen, product]);

  const loadPresentations = async () => {
    if (!product) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await posService.getProductPresentations(product.id);
      
      if (response.success && response.data.length > 0) {
        // Filtrar: delivery muestra solo delivery, normal muestra solo no-delivery
        const filtered = response.data.filter(p =>
          deliveryOnly ? p.isDelivery : !p.isDelivery
        );

        setPresentations(filtered.map(p => ({
          id: p.id,
          name: p.name,
          price: p.price,
          description: p.description
        })));
      } else {
        // Fallback: usar presentación básica si no hay presentaciones en BD
        setPresentations([
          {
            id: 'default',
            name: 'Estándar',
            price: product.price,
            description: 'Presentación estándar del producto'
          }
        ]);
      }
    } catch (error) {
      setError('Error al cargar presentaciones');
      console.error('Error loading presentations:', error);
      
      // Fallback en caso de error
      setPresentations([
        {
          id: 'default',
          name: 'Estándar',
          price: product.price,
          description: 'Presentación estándar del producto'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleSelectPresentation = (option: PresentationOption) => {
    onSelectPresentation(product!, option, quantity);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Seleccionar Presentación</DialogTitle>
          <p className="text-sm text-gray-600">{product.name}</p>
        </DialogHeader>

        {/* Control de cantidad global */}
        <div className="flex items-center justify-center gap-4 py-4 border-b">
          <Button
            variant="outline"
            className="h-12 w-12 p-0 text-lg font-semibold shadow-md transition-all duration-200 active:scale-95 touch-manipulation"
            onClick={() => handleQuantityChange(quantity - 1)}
            disabled={quantity <= 1}
          >
            <Minus className="h-6 w-6" />
          </Button>

          <span className="font-bold text-2xl min-w-[3ch] text-center bg-gray-50 px-4 py-2 rounded-lg">
            {quantity}
          </span>

          <Button
            variant="outline"
            className="h-12 w-12 p-0 text-lg font-semibold shadow-md transition-all duration-200 active:scale-95 touch-manipulation"
            onClick={() => handleQuantityChange(quantity + 1)}
          >
            <Plus className="h-6 w-6" />
          </Button>
        </div>

        <div className="space-y-4 mt-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button
                onClick={loadPresentations}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                Reintentar
              </Button>
            </div>
          ) : presentations.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {presentations.map((option) => {
                return (
                  <Card
                    key={option.id}
                    onClick={() => handleSelectPresentation(option)}
                    className="transition-all duration-200 cursor-pointer hover:shadow-lg hover:border-orange-500 active:scale-95 touch-manipulation"
                  >
                    <CardContent className="p-3 flex flex-col justify-center items-center gap-1">
                      <h4 className="font-semibold text-gray-900 text-sm text-center">
                        {option.name}
                      </h4>
                      <span className="font-bold text-macartur-primary text-sm">
                        S/ {option.price.toFixed(2)}
                      </span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No hay presentaciones disponibles</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            className="h-12 px-8 text-base font-semibold shadow-md transition-all duration-200 active:scale-95 touch-manipulation"
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};